<template>
  <router-view />
</template>

<script setup lang="ts">
import { checkToken, wxLogin } from '@/api/login'
import { useUserStore } from '@/stores/user'
import { withLoading, showToastNextTick } from '@/utils/request'

const userStore = useUserStore()

onMounted(() => {
  window.addEventListener('wecom-auth-done', () => {
    void userStore.loadUser(true)
  })
  if (!import.meta.env.DEV) {
    startWecomLogin()
  }
})

/** 读取 query 中的 code */
const getQueryCode = () => {
  const p = new URLSearchParams(location.search)
  return String(p.get('code') || '')
}

/** 构建企微 OAuth 跳转地址 */
const buildOAuthUrl = () => {
  const corpId = import.meta.env.VITE_WECOM_CORP_ID as string
  const agentId = import.meta.env.VITE_WECOM_AGENT_ID as string
  const redirect = encodeURIComponent(location.origin + location.pathname)
  const state = 'wecom_login'
  if (!corpId || !agentId) return ''
  return `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${corpId}&redirect_uri=${redirect}&response_type=code&scope=snsapi_userinfo&agentid=${agentId}&state=${state}#wechat_redirect`
}

/** 设置 cookie token */
const setAuthorizationCookie = (token: string) => {
  if (!token) return
  const secure = location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = `Authorization=${encodeURIComponent(token)}; Path=/; SameSite=Lax${secure}`
}

/** 从 cookie 读取 token（仅返回纯 token） */

const getTokenFromCookie = () => {
  const m = document.cookie.match(/(?:^|;\s*)Authorization=([^;]+)/)
  if (!m) return ''

  // 原始 token（可能含 Bearer 前缀）
  let token = decodeURIComponent(m[1] || '').trim()

  // 去掉 Bearer 前缀（不区分大小写）并去掉后面的空格
  if (/^Bearer\s+/i.test(token)) {
    token = token.replace(/^Bearer\s+/i, '')
  }

  return token
}

/** 清除 cookie 中的 token */
const clearAuthorizationCookie = () => {
  document.cookie = `Authorization=; Max-Age=0; Path=/; SameSite=Lax`
}

/** 清除 URL 中的 code，避免刷新重复登录 */
const removeCodeFromUrl = () => {
  const url = new URL(location.href)
  url.searchParams.delete('code')
  history.replaceState(null, '', url.toString())
}

/** 入口：企微登录流程 */
const startWecomLogin = async () => {
  try {
    await withLoading(
      async () => {
        // --- A. 优先读取 cookie token ---
        const token = getTokenFromCookie()
        if (token) {
          // 有 token → 仅此情况下才校验（silent: 失败视为无效，不弹默认错误 toast）
          try {
            const res = await checkToken(token, { silent: true })
            const valid = (res as any)?.code === 200 ? Boolean((res as any)?.data) : false
            if (valid) {
              sessionStorage.removeItem('wecom_oauth_retry')
              sessionStorage.setItem('wecom_login_done', '1')
              removeCodeFromUrl()
              return
            }
          } catch {
            // 忽略错误，视为无效
          }

          // token 无效或校验失败 → 清理后进入登录流程
          clearAuthorizationCookie()
          sessionStorage.removeItem('wecom_login_done')
        } else {
          // 没有 token → 不做任何校验，直接进入登录流程
          sessionStorage.removeItem('wecom_login_done')
        }

        // --- B. 防止重复登录：如果已标记完成则不再走登录 ---
        if (sessionStorage.getItem('wecom_login_done') === '1') {
          removeCodeFromUrl()
          return
        }

        // --- C. 有 code → 调后端换 token ---
        const code = getQueryCode()
        if (code) {
          try {
            const res = await wxLogin(code, { silent: true })
            if ((res as any)?.code === 200) {
              const newToken = String((res as any)?.data?.token || '')
              setAuthorizationCookie(newToken)
              sessionStorage.removeItem('wecom_oauth_retry')
              sessionStorage.setItem('wecom_login_done', '1')
              removeCodeFromUrl()
              window.dispatchEvent(new Event('wecom-auth-done'))
              return
            }
          } catch (error: any) {
            // 失败则继续走 OAuth 获取新 code，最多重试 2 次
            const key = 'wecom_oauth_retry'
            let count = Number(sessionStorage.getItem(key) || '0') || 0
            count += 1
            sessionStorage.setItem(key, String(count))
            if (count >= 2) {
              const msg = error.response?.data?.message
              // 用 next tick，因外层 withLoading 会紧接着关 loading
              showToastNextTick(msg || '登录异常')
              throw error
            }
          }
        }

        // --- D. 无 code → 跳转 OAuth 获取 code ---
        const oauth = buildOAuthUrl()
        if (oauth) {
          location.replace(oauth)
        }
      },
      { message: 'Please wait...' },
    )
  } catch (error) {
    console.error(error)
  }
}
</script>

<style scoped></style>
